import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { UserType } from '@shared-features/enums/user-type.enum';
import { takeUntil } from 'rxjs/operators';
import { ShippingType } from '@shared-features/enums/shipping-type.enum';
import { MobileLengthPerCode } from '@shared-features/enums/mobile-length-per-code.enum';
import { UserStatus } from '@shared-features/enums/user-status.enum';
import {
  EmailValidator,
  MobileNumberLengthValidator,
  noWhitespaceValidator,
  PhoneNumberValidatorWithException,
} from '@shared/validators/form-validators';
import { FunctionUtil } from '@shared/utils/function.util';
import { Router } from '@angular/router';
import { UsersApiService } from '@features/users/services/users-api.service';
import { SharedApiService } from '@shared-features/services/shared-api.service';
import { RolesApiService } from '@shared-features/services/roles-api.service';
import { forkJoin } from 'rxjs';
import { MatLegacyCheckboxChange } from '@angular/material/legacy-checkbox';
import { Role } from '@features/auth/models/role';
import { User } from '@features/users/models/user';
import { Country } from '@shared-features/models/country';
import { UpdateUserDto } from '@features/users/dto/update-user.dto';

type action = 'add' | 'update';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() userType: UserType;
  @Input() id: number;
  @Input() user: User;
  @Input() action: action = 'add';
  @Input() shippingType: ShippingType;
  @Output() close = new EventEmitter();
  @Output() update = new EventEmitter();

  form: FormGroup;
  roles: Role[] = [];
  countries: Country[] = [];
  userStatus: Omit<UserStatus, 'All'>;

  MobileLengthPerCode = MobileLengthPerCode;
  ShippingType = ShippingType;
  UserType = UserType;
  emailValidators = [EmailValidator];
  ShipmentCreationTemplateTypes = ShipmentCreationTemplateTypes;

  constructor(
    private router: Router,
    private usersApiService: UsersApiService,
    private sharedApiService: SharedApiService,
    private rolesApiService: RolesApiService,
    private builder: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.userStatus = Object.keys(UserStatus).reduce((group, status) => {
      if (status !== 'All') {
        group[status] = UserStatus[status];
      }
      return group;
    }, {} as UserStatus);
  }

  ngOnChanges({ userType }: SimpleChanges): void {
    if (userType && userType?.currentValue !== userType?.previousValue) {
      this.load(
        forkJoin([
          this.sharedApiService.getAllCountries(),
          this.rolesApiService.getActiveRoles(
            this.userType,
            this.userType === UserType.ADMIN ? [UserType.CONTROL_TOWER] : []
          ),
        ])
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(([countries, roles]) => {
          this.countries = countries;
          this.roles = roles;
          this.initForm();
        });
    }
  }

  initForm(): void {
    const mobileNum = this.user?.mobileNumber;
    this.form = this.builder.group({
      firstName: [this.user?.firstName || '', [Validators.required, noWhitespaceValidator]],
      lastName: [this.user?.lastName || '', [Validators.required, noWhitespaceValidator]],
      mobileNumber: [
        mobileNum?.split('-')[1] || '',
        [
          Validators.required,
          PhoneNumberValidatorWithException,
          MobileNumberLengthValidator(() => this.form?.value?.countryCode),
        ],
      ],
      email: [this.user?.email || '', this.emailValidators],
      country: [this.countries.find((c) => c.id === this.user?.country?.id) || '', []],
      countryCode: [
        mobileNum?.split('-')[0] || FunctionUtil.getSaudiArabiaPhoneKey(this.countries),
        [Validators.required],
      ],
      roles: [this.roles.find((r) => r.id === this.user?.roles[0]?.id) || '', []],
      status: [this.user?.status, []],
      shippingType: [this.user?.shippingType || this.shippingType || ShippingType.All, []],
      allowToLogin: [this.user?.allowToLogin || false, []],
    });

    if (this.user?.allowToLogin) {
      this.emailValidators.push(Validators.required);
      this.form.controls.email.setValidators(this.emailValidators);
    }
  }

  onAllowToLoginChanges(event: MatLegacyCheckboxChange) {
    const email = this.form.controls.email;
    if (event.checked) {
      this.emailValidators.push(Validators.required);
    } else {
      this.emailValidators.pop();
    }
    email.setValidators(this.emailValidators);
    email.updateValueAndValidity();
    email.markAsTouched();
  }

  onAddUser() {
    if (this.router.url.includes('FleetsB2c')) {
      const FleetsB2c = this.roles.find((x) => x.name == 'Fleet B2C');
      this.form.value['roles'] = FleetsB2c;
    }
    const { countryCode, mobileNumber, roles, ...formValue } = this.form.value;
    const user = Object.assign(new User(), this.user, formValue, {
      mobileNumber: countryCode + '-' + mobileNumber,
      roles: [roles],
    });
    this.load(this.sharedApiService.addUser(user, roles?.userType, this.id), {
      successMessagePath: 'SuccessMessages.UserAddedSuccessfully',
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.close.emit();
        this.update.emit({ user, shippingType: this.form.value.shippingType });
      });
  }

  onUpdateUser(): void {
    const { countryCode, mobileNumber, roles, ...formValue } = this.form.value;
    const user = Object.assign(new User(), this.user, formValue, {
      mobileNumber: countryCode + '-' + mobileNumber,
      roles: [roles],
    });
    const updateUserDto = new UpdateUserDto(user);
    const updateUserPayload = FunctionUtil.getObjectDiff(this.user as unknown as UpdateUserDto, updateUserDto, [
      'status', 
      'firstName', 
      'lastName', 
      'roleIds', 
      'id', 
      'userType'
    ]);

    this.load(this.usersApiService.updateUser(updateUserPayload), {
      successMessagePath: 'SuccessMessages.UserUpdatedSuccessfully',
    }).subscribe((user) => {
      this.update.emit({ user, shippingType: this.form.value.shippingType });
    });
  }
}
